import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { IResourceField } from '@ispm/shared/components/textual-content/textual-content.component';
import {
  IConditionConfig,
  IDynamicFormItemIcon,
  IDynamicFormItemTooltipPattern,
  IDynamicFormItemTooltipWidget
} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { has, isEmpty, remove as lodashRemove, isString } from 'lodash';
import { BlurItemService } from '@ispm/shared/services/blur-item.service';
import { InterpolateService, InterpolationOptions } from '@ispm/shared/services/interpolate.service';
import { run } from 'formula';

@Component({
  selector: 'gen-editable-parameter',
  templateUrl: './editable-parameter.component.html',
  styleUrls: ['./editable-parameter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class EditableParameterComponent implements AfterContentInit, OnInit, OnDestroy, OnChanges {
  private static COUNTER: number = 0;
  private _id: string = (EditableParameterComponent.COUNTER++).toString();
  @ViewChild('parser', { read: ViewContainerRef, static: false}) entry: ViewContainerRef;
  @Input() hideEditIcon: boolean = false;
  @Input() isEditableField: boolean = false;
  @Input() tooltipText: string;
  @Input() tooltipConfiguration?: IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget;
  @Input() tooltipData: any;
  @Input() formId: string;
  @Input() isItDefault: boolean;
  @Input() tooltipPosition: string = 'BOTTOM';
  @Input() displayTooltip: boolean = true;
  @Input() invalid: boolean = false;
  @Input() required: boolean = false;
  @Input() label: string;
  @Input() promptSeparator: boolean;
  @Input() valueStyle: any;
  @Input() title: string = null;
  @Input() interpolateObject: any;
  @Input() classes: string = '';
  @Input() key: string;
  @Input() fieldType: string;
  @Input() fieldAttributes: any;
  @Input() requiredMessage: string = 'Required information missing';
  @Input() linkPrefix: string;
  @Input() editableInitMode: boolean = null;
  @Input() stayEditable: boolean = false;
  @Input() ignoreMarginRight: boolean = false;
  @Input() separator: string;
  @Input() icon: IDynamicFormItemIcon;
  @Input() interpolationOptions: InterpolationOptions = null;
  @Input() hyphenate: boolean = false;
  @Input() tooltipWrapperClass: string = '';
  @Input() additionalSearchValues: any;
  @Input() isTriggerBlurItem: boolean = true;
  @Output() update: EventEmitter<any> = new EventEmitter();
  @Output() callback: EventEmitter<any> = new EventEmitter();
  @Output() linkClick: EventEmitter<string> = new EventEmitter<string>();

  private _editableItemEmptyValueText: string = 'Click to edit...';

  private get blurElement(): HTMLElement {
    return this.element.nativeElement.parentElement;
  }

  @Input() set editableItemEmptyValueText(text: string) {
    this._editableItemEmptyValueText = text ? text : 'Click to edit...';
  }

  get editableItemEmptyValueText() {
    return this._editableItemEmptyValueText;
  }

  _defaultList: Array<any>;

  @Input()
  set defaultList(list: Array<any>) {
    this._defaultList = list;
  }

  private _displayPattern: string | Array<IConditionConfig>;

  @Input()
  set displayPattern(displayPattern: string | Array<IConditionConfig>) {
    this._displayPattern = displayPattern;
    // This is required to trigger the textual component
    if (this.resourceFieldConfig && this.resourceFieldConfig.pattern) {
      this.resourceFieldConfig.pattern = displayPattern as string;
      this.changeDetector.detectChanges();
    }
  }

  get displayPattern() {
    return this._displayPattern;
  }

  @Input()
  set disabled(disabled: boolean) {
    if (disabled && !this.stayEditable) {
      this.editable = false;
    }
  }


  @Input()
  set stayEditableOn(classList: string) {
    this._editableOnSelectors = classList.split(',');
  }

  // To extend the functionality of the outside click directive this new input was added
  //  to add additional selectors
  @Input()
  set excludeClicksOn(v) {
    this._excludeClicksOn = v;
    if (!v) {
      return;
    }
    this.clicksExcluded += this._excludeClicksOn;
  }

  get excludeClicksOn() {
    return this._excludeClicksOn;
  }

  // To extend the functionality of the outside click directive this new input was added
  //  to exclude certain selectors from holding the click event
  @Input()
  set checkClicksOn(v) {

    this._checkClicksOn = v;
    if (!v) {
      return;
    }
    const selectors = { };
    v.split(',').forEach(selector => selectors[selector] = null);
    const splitClicksExcluded = this.clicksExcluded.split(',');

    // Remove function is used here instead of the `without` function,
    //  because the `without` function requires a spread operator, something that might be an issue in IE11
    const filteredExclude = lodashRemove(splitClicksExcluded, (selector) => selectors[selector] !== null);
    this.clicksExcluded = filteredExclude.join(',');
  }

  get checkClicksOn() {
    return this._checkClicksOn;
  }

  get stayEditableOn() {
    return this._editableOnSelectors.join(',');
  }

  @Input()
  set formMode(formMode: boolean) {
    this.formModeFlag = formMode;
    if (formMode) {
      this.Edit(true, false);
    } else {
      this.editable = this.editableInitMode;
    }
  }

  get formMode() {
    return this.formModeFlag;
  }

  @Input()
  set showAsLink(value: boolean) {
    this._showAsLink = value ? value : false; // in case showAsLink is undefined
  }

  get showAsLink() {
    return this._showAsLink;
  }

  @Input()
  set showAsTextWithLink(value: boolean) {
    this._showAsTextWithLink = value ? value : false; // in case _showAsTextWithLink is undefined
  }

  get showAsTextWithLink() {
    return this._showAsTextWithLink;
  }

  @Input()
  set parentHandleLink(value: boolean) {
    this._parentHandleLink = value ? value : false; // in case parentHandleLink is undefined
  }

  get parentHandleLink() {
    return this._parentHandleLink;
  }

  @Input()
  set showLinkSuffix(value: boolean) {
    this._showLinkSuffix = value === undefined ? true : value; // true by default
  }

  get showLinkSuffix() {
    return this._showLinkSuffix;
  }

  get linkName() {
    if (!this._showLinkSuffix) {
      this._linkName = this._value;
    } else if (!this._linkName) {
      let name: string = this._value;
      const pos = name.lastIndexOf('/');
      if (pos > 0) {
        name = name.slice(pos + 1);
      }
      this._linkName = name;
    }
    return this._linkName;
  }

  get linkAddress() {
    if (!this._linkAddress) {
      if (this._value) {
        this._linkAddress = this.linkPrefix ? this.linkPrefix + this._value : this._value;
      }
    }
    return this._linkAddress;
  }

  @Input()
  set value(value) {
    if (Array.isArray(value) && value.length > 0 && has(value[0], 'cssClass')) {
      this._value = value;
      this.handler = 'array';
    } else {
      this._value = this.getValueDisplayText(value);
      this.handler = 'text';
    }

    // this feature helps to write mutiple display patterns based on conditions.
    this.handleConditionalDisplayPattern();


    this._linkName = '';
    this._linkAddress = '';
    this.resourceFieldConfig = {
      path: this.displayPattern ? null : this.key,
      pattern: this.displayPattern as string,
      link: this.linkAddress,
      type: this.getResourceFieldType(),
      separator: this.separator,
      cssClasses: this._cssClass,
      parentHandleLink: this.parentHandleLink,
      showLinkSuffix: this._showLinkSuffix,
      interpolationOptions: this.interpolationOptions,
      hyphenate: this.hyphenate,
      itemCssClass:this._cssClass,
    };

  }

  private getResourceFieldType(): string {
    let type;
    if (this.showAsLink) {
      type = 'link';
    } else {
      if (this.showAsTextWithLink) {
        type = 'text-with-link';
      } else {
        type = 'text';
      }
    }
    return type;
  }

  get value() {
    if (this.displayPattern && this.interpolateObject) {
      return this.interpolateObject;
    } else if (this.showAsLink) {
      return this.linkName;
    } else {
      return this._value;
    }
  }

  constructor(
    private element: ElementRef,
    private _zone: NgZone,
    private changeDetector: ChangeDetectorRef,
    private interpolator: InterpolateService
  ) {

  }

  public handler: 'array' | 'text';
  public resourceFieldConfig: IResourceField;
  public editable: boolean = false;
  public input: any;
  public formModeFlag: boolean = false;
  public clicksExcluded = '.p-autocomplete-token,.main_value,.Auto_Value,.p-autocomplete-item,.p-autocomplete-items,.p-autocomplete-panel,.p-autocomplete-token-icon,.p-autocomplete-token-icon:before,.p-datepicker-year,.p-datepicker-month,.p-datepicker-next,.p-datepicker-prev,.p-datepicker-header,.pt-input';
  public readOnlyView: boolean = false;
  private emptyValue: boolean = false;
  private _excludeClicksOn: string;
  private _checkClicksOn: string;
  private _editableOnSelectors: Array<string> = [];
  private _showAsLink: boolean = false;
  private _showAsTextWithLink: boolean = false;
  private _parentHandleLink: boolean = false;
  private _linkName: string;
  private _showLinkSuffix: boolean = true;
  private _linkAddress: string;
  private _cssClass: string = 'default-editable-value';
  private _value: any;

  @Input() isParamValid: () => boolean = () => true;

  ngAfterContentInit(): void {
    this.input = this.element.nativeElement.querySelector('select,input');
    if (this.formMode) {
      this.Edit(true);
    }
  }

  onInputBlur($event) {
    if (this.isEditableField) {
      // !this.formMode && - add to the if when toggle inputs to text on blur shouldn't occur anymore
      if ($event && !($event.type && $event.type === 'updateValue')) {
        if (this.isParamValid() && !(this.required && this.emptyValue) && !this.stayEditable) {
          this.editable = false;
          BlurItemService.removeBlurItem(this._id);
        }
      }
      if (!($event && $event.type && $event.type === 'updateValue') && (!this.editable)) {
        this.update.emit();
      }
    }
  }

  Edit(force: boolean = false, forceReadOnlyView?: boolean) {
    if (this.isEditableField || force) {
      this.editable = true;
      this.readOnlyView = forceReadOnlyView ? false : this.readOnlyView;
      BlurItemService.addBlurItem(this._id, this.blurElement);
      this._zone.runOutsideAngular(() => {
        setTimeout(() => {
          if (!this.input) {
            this.input = this.element.nativeElement.querySelector('select,input,textarea');
          }
          if (!force) {
            this.input.focus();
          }
        }, 0);
      });
    }
  }

  /**
   * This handles the display pattern provided as a array with IConditionConfig which has the formula and pattern.
   * It evaluates the formula provided ,incase the iteration returned truthy it would set the pattern to display pattern.
   * The displayPattern having initially an array of IConditionConfig would be finalized with the pattern.
   * The IConditionConfigs should be ordered in way that initial conditions might be negative checks.
   */
  private handleConditionalDisplayPattern() {
    if (Array.isArray(this.displayPattern) && this.displayPattern.length > 0) {
      this.displayPattern = this.fillingConditionalDisplayPattern(this.displayPattern);
    }
    if (this.tooltipConfiguration && Array.isArray(this.tooltipConfiguration.tooltipDisplayPattern)
      && this.tooltipConfiguration.tooltipDisplayPattern.length > 0) {
      this.tooltipConfiguration.tooltipDisplayPattern =
        this.fillingConditionalDisplayPattern(this.tooltipConfiguration.tooltipDisplayPattern);
    }
  }

  private fillingConditionalDisplayPattern(displayPattern: Array<IConditionConfig>) {
    let patternToSet: string = '';
    let iterationSuccess: boolean = false;
    try {
      displayPattern.forEach((patternConfig: IConditionConfig) => {
        if (!iterationSuccess) {
          if (Array.isArray(patternConfig.formula)) {
            const conditionItemSuccess: boolean[] = [];
            patternConfig.formula.forEach(conditionItem => {
              conditionItemSuccess.push(this.runFormula(conditionItem));
            });
            iterationSuccess = conditionItemSuccess.every(value => !!value);
          } else {
            iterationSuccess = this.runFormula(patternConfig);
          }
          if (iterationSuccess) {
            patternToSet = patternConfig.pattern;
          }
        }
      });
    } catch (exception) {
      patternToSet = '';
    }
    return patternToSet;
  }

  private runFormula(conditionItem) {
    let formulaResult = false;
    const interpolatedString = this.interpolator.interpolate(conditionItem.formula, this.interpolateObject, true);
    const returnValue = run(interpolatedString, {});
    if (returnValue && !(returnValue instanceof Error)) {
      formulaResult = true;
    }
    return formulaResult;
  }

  private getValueDisplayText(value) {
    this.emptyValue = false;
    this._cssClass = 'default-editable-value';
    if (value != undefined) {
      if (value instanceof Date) {
        return (<any>value).toPlatformFormat(true);
      } else {
        return value;
      }
    } else {
      this.emptyValue = true;
      return this.required
        ? this.requiredMessage || value
        : '';
    }
  }

  linkClicked($event) {
    this.linkClick.emit($event);
  }

  ngOnDestroy(): void {
    BlurItemService.removeBlurItem(this._id);
    this.input = null;
    this.entry = null;
  }

  needShowEmptyText(value: any): boolean {
    return this.isEditableField && (
      (typeof value === 'number' && isNaN(value))
      || (isEmpty(value) && typeof value !== 'number') // Lodash: isEmpty of number always returns TRUE irrespective of its value
      || (typeof value === 'string' && value.toString() === '')
      || (!Array.isArray(value) && typeof value === 'object' && value.hasOwnProperty('code') && (value.code === undefined || value.code === ''))
    );
  }

  ngOnInit() {
    if (!this.formMode) {
      this.editable = this.editableInitMode;
    }

    if (!isEmpty(this.value) && this.fieldAttributes && this.fieldAttributes['[readOnlyView]']) {
      this.readOnlyView = this.getReadOnlyViewValue();
    }
    this.resourceFieldConfig = {
      path: this.displayPattern ? null : this.key,
      pattern: this.displayPattern as string,
      link: this.linkAddress,
      type: this.getResourceFieldType(),
      separator: this.separator,
      cssClasses: this._cssClass,
      parentHandleLink: this.parentHandleLink,
      showLinkSuffix: this._showLinkSuffix,
      interpolationOptions: this.interpolationOptions,
      hyphenate: this.hyphenate,
      itemCssClass:this._cssClass
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.linkPrefix){
      if (changes.linkPrefix.previousValue != changes.linkPrefix.currentValue){
        this._linkAddress = ''
      }
    }
  }

  private getReadOnlyViewValue(): boolean {
    let readOnlyView = false;
    if (this.additionalSearchValues.length > 0) {
      outer_loop: for (let i = 0; i < this.additionalSearchValues.length; i++) {
        inner_loop: for (let j = 0; j < this.additionalSearchValues[i].revisions.length; j++) {
          if (this.additionalSearchValues[i].revisions[j].code === this._value.revisionId && j !== this.additionalSearchValues[i].revisions.length - 1) {
            readOnlyView = false;
            break outer_loop;
          } else {
            readOnlyView = true;
          }
        }
      }
    } else {
      readOnlyView = true;
    }
    return readOnlyView;
  }

  showRequiredMark(): boolean {
    if (this.required) {
      if (isString(this.label)) {
        this.label = this.label.trim();
      }

      return isEmpty(this.label);
    }

    return false;
  }

}
